import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealDark: '#0087A8',
  bg: '#F0F7FA', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF', purple: '#8B5CF6',
};

const STATUS_CONFIG = {
  pending:        { label: 'Awaiting Payment',   color: C.warning, bg: '#FEF3C7', icon: '⏳' },
  proof_uploaded: { label: 'Proof Submitted',     color: '#3B82F6', bg: '#EFF6FF', icon: '📎' },
  confirmed:      { label: 'Payment Confirmed',   color: C.success, bg: '#D1FAE5', icon: '✅' },
  rejected:       { label: 'Proof Rejected',      color: C.error,   bg: '#FEE2E2', icon: '❌' },
  overdue:        { label: 'OVERDUE',             color: C.error,   bg: '#FEE2E2', icon: '🚨' },
};

// Plan metadata (features, recommended badge, tier)
const PLAN_META = {
  starter:        { name: 'Starter',        color: '#64748B', badge: null,            features: ['Up to 50 students','All AI lessons','Basic reporting','Email support'] },
  growth:         { name: 'Growth',         color: C.teal,    badge: 'POPULAR',       features: ['Up to 200 students','All AI lessons','Advanced reporting','Priority email support','Multiple tutors'] },
  pro:            { name: 'Pro',            color: C.purple,  badge: 'UNLIMITED',     features: ['Unlimited students','All AI lessons','Full analytics','Priority support','Custom branding'] },
  school:         { name: 'School',         color: '#F59E0B', badge: null,            features: ['Up to 200 students','Custom student pricing','All AI lessons','Reporting dashboard','Priority support'] },
  academy:        { name: 'Academy',        color: '#EF4444', badge: 'UNLIMITED',     features: ['Unlimited students','Custom student pricing','All AI lessons','Full analytics','Priority support','White label options'] },
  enterprise:     { name: 'Enterprise',     color: '#7C3AED', badge: 'WHITE LABEL',   features: ['Unlimited students','Full white label','Custom pricing','Dedicated account manager','SLA guarantee','API access'] },
  school_pro:     { name: 'School Pro',     color: '#F59E0B', badge: null,            features: ['Up to 200 students','Custom packages & rates','All AI lessons','Advanced reporting','Priority support'] },
  academy_pro:    { name: 'Academy Pro',    color: '#EF4444', badge: 'UNLIMITED',     features: ['Unlimited students','Custom packages & rates','Full analytics','Priority support','White label options'] },
  enterprise_pro: { name: 'Enterprise Pro', color: '#7C3AED', badge: 'WHITE LABEL',   features: ['Unlimited students','Full white label','Custom everything','Dedicated account manager','SLA guarantee'] },
};

function BankRow({ label, value, mono, copy, fullWidth }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto', background: '#F8FAFC', borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: C.grey, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.dark, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
        {copy && (
          <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: copied ? '#D1FAE5' : C.white, color: copied ? '#065F46' : C.grey, cursor: 'pointer', fontWeight: 600 }}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ fontSize: 13, color: C.grey }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: highlight ? C.success : C.dark, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function UsageBar({ current, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const color = pct >= 90 ? C.error : pct >= 70 ? C.warning : C.success;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
        <span style={{ color: C.grey }}>Students enrolled</span>
        <span style={{ fontWeight: 700, color }}>{current} / {max > 0 ? max : '∞'}</span>
      </div>
      {max > 0 && (
        <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: 8, background: color, borderRadius: 4, width: `${pct}%`, transition: 'width 0.5s' }} />
        </div>
      )}
      {max > 0 && pct >= 80 && (
        <div style={{ marginTop: 6, fontSize: 12, color: pct >= 100 ? C.error : C.warning, fontWeight: 600 }}>
          {pct >= 100 ? '🔴 Limit reached — upgrade to add more students' : `⚠️ ${100 - pct}% capacity remaining`}
        </div>
      )}
    </div>
  );
}

function PlanCard({ planId, info, meta, isCurrent, isPending, onSelect }) {
  const m = meta || { name: planId, color: C.teal, features: [] };
  const maxS = info.max_students > 0 ? `Up to ${info.max_students} students` : 'Unlimited students';
  return (
    <div style={{
      background: C.white, borderRadius: 14,
      border: `2px solid ${isCurrent ? m.color : isPending ? C.warning : C.border}`,
      padding: 20, position: 'relative', transition: 'border-color 0.2s',
      boxShadow: isCurrent ? `0 4px 16px ${m.color}28` : '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {isCurrent && (
        <div style={{ position: 'absolute', top: -12, left: 16, background: m.color, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>CURRENT PLAN</div>
      )}
      {isPending && (
        <div style={{ position: 'absolute', top: -12, left: 16, background: C.warning, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>⏳ PENDING</div>
      )}
      {m.badge && !isCurrent && !isPending && (
        <div style={{ position: 'absolute', top: -12, right: 16, background: m.color, color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{m.badge}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
        <span style={{ fontWeight: 800, fontSize: 16, color: C.dark }}>{m.name}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: m.color, marginBottom: 4 }}>
        ${info.monthly_usd}<span style={{ fontSize: 13, fontWeight: 500, color: C.grey }}>/mo</span>
      </div>
      <div style={{ fontSize: 13, color: C.grey, marginBottom: 14, fontWeight: 600 }}>{maxS}</div>
      <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {m.features.map(f => (
          <li key={f} style={{ fontSize: 12, color: C.dark, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <span style={{ color: C.success, flexShrink: 0 }}>✓</span>{f}
          </li>
        ))}
      </ul>
      {!isCurrent && (
        <button onClick={() => onSelect(planId, info)}
          style={{
            width: '100%', padding: '10px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
            background: isPending ? C.warning : m.color, color: '#fff', border: 'none',
          }}>
          {isPending ? 'Change Pending…' : 'Select Plan'}
        </button>
      )}
    </div>
  );
}

function ConfirmPlanModal({ currentPlan, newPlanId, newPlanInfo, newPlanMeta, onConfirm, onCancel, loading }) {
  const m = newPlanMeta || { name: newPlanId };
  const isUpgrade = newPlanInfo.monthly_usd > (currentPlan?.monthly_fee_usd || 0);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: C.dark }}>{isUpgrade ? '⬆️ Upgrade Plan' : '⬇️ Downgrade Plan'}</h2>
        <p style={{ color: C.grey, fontSize: 14, margin: '0 0 20px' }}>
          {isUpgrade
            ? `You are upgrading to ${m.name} at $${newPlanInfo.monthly_usd}/mo. Your plan will be activated once SpeakIQ confirms your payment.`
            : `You are downgrading to ${m.name} at $${newPlanInfo.monthly_usd}/mo. The change takes effect after super admin approval.`}
        </p>
        <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
            <span style={{ color: C.grey }}>Current plan</span>
            <span style={{ fontWeight: 600 }}>{currentPlan?.plan} · ${currentPlan?.monthly_fee_usd}/mo</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
            <span style={{ color: C.grey }}>New plan</span>
            <span style={{ fontWeight: 700, color: (newPlanMeta||{}).color || C.teal }}>{m.name} · ${newPlanInfo.monthly_usd}/mo</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: C.grey }}>Max students</span>
            <span style={{ fontWeight: 600 }}>{newPlanInfo.max_students > 0 ? newPlanInfo.max_students : 'Unlimited'}</span>
          </div>
        </div>
        <div style={{ background: '#FFF7ED', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#92400E' }}>
          📋 <strong>What happens next:</strong> A payment request will be created for ${newPlanInfo.monthly_usd}/mo.
          Upload your payment proof and SpeakIQ will activate your new plan within 1 business day.
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={onCancel} disabled={loading}
            style={{ flex: 1, padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.dark, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ flex: 2, padding: '12px', borderRadius: 8, border: 'none', background: C.teal, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            {loading ? 'Requesting…' : 'Confirm Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBilling() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [tab, setTab]           = useState('plan');
  const [selectedPlan, setSelectedPlan] = useState(null);  // {planId, info}
  const [planLoading, setPlanLoading] = useState(false);
  const fileRef = useRef();

  const [outstanding, setOutstanding] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const overdueFileRefs = React.useRef({});

  const load = async () => {
    try {
      const [res, outRes] = await Promise.all([
        api.get("/api/admin/billing/info"),
        api.get("/api/admin/billing/outstanding").catch(() => ({ data: [] })),
      ]);
      setOutstanding(outRes.data || []);
      setData(res.data);
    } catch {
      setError('Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !data?.current_payment) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true); setError(''); setSuccess('');
    try {
      await api.post(`/api/admin/billing/${data.current_payment.id}/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Payment proof uploaded! The SpeakIQ team will verify and confirm within 1 business day.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadInvoice = (paymentId, label) => {
    const token = localStorage.getItem('speakiq_token') || '';
    fetch('/api/admin/billing/' + paymentId + '/invoice', {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(r) {
      if (!r.ok) throw new Error('Failed');
      return r.blob();
    }).then(function(blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SpeakIQ-Invoice-' + (label || paymentId) + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
    }).catch(function() { setError('Could not download invoice. Please try again.'); });
  };

  const [previewUrl, setPreviewUrl] = React.useState(null);

  const previewInvoice = async (paymentId) => {
    try {
      const r = await api.get('/api/admin/billing/' + paymentId + '/share-url');
      setPreviewUrl(r.data.share_url);
    } catch { setError('Could not load invoice preview'); }
  };

  const handlePlanSelect = (planId, info) => setSelectedPlan({ planId, info });

  const handlePlanConfirm = async () => {
    if (!selectedPlan) return;
    setPlanLoading(true); setError(''); setSuccess('');
    try {
      const res = await api.post('/api/admin/billing/request-plan-change', { new_plan: selectedPlan.planId });
      setSuccess(res.data.message || 'Plan change requested!');
      setSelectedPlan(null);
      await load();
      setTab('billing');
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to request plan change.');
    } finally {
      setPlanLoading(false);
    }
  };

  const handleCancelPlanChange = async () => {
    setError(''); setSuccess('');
    try {
      await api.post('/api/admin/billing/cancel-plan-change');
      setSuccess('Plan change request cancelled.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to cancel plan change.');
    }
  };

  const bank = data?.bank_settings;
  const payment = data?.current_payment;
  const tenant = data?.tenant;
  const plans = data?.plans || {};
  const status = payment ? (STATUS_CONFIG[payment.status] || STATUS_CONFIG.pending) : null;
  const isOverdue = data?.days_overdue > 0 && payment?.status !== 'confirmed';
  const canUpload = payment && ['pending', 'overdue', 'rejected'].includes(payment.status);
  const studentCount = tenant?.student_count || 0;
  const maxStudents = tenant?.max_students || 0;

  const TABS = [
    { id: 'plan', label: '📋 Plan & Limits' },
    { id: 'billing', label: '💳 Billing & Payment' },
    { id: 'history', label: '🕐 Payment History' },
    { id: 'outstanding', label: outstanding.length > 0 ? `⚠️ Outstanding (${outstanding.length})` : '⚠️ Outstanding' },
  ];

  return (
    <Layout role="tenant_admin">
      <div style={{ padding: 'clamp(16px,4vw,32px)', maxWidth: 1000, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.dark, margin: 0 }}>Subscription & Billing</h1>
          <p style={{ color: C.grey, margin: '4px 0 0', fontSize: 14 }}>Manage your SpeakIQ platform plan and payments</p>
        </div>

        {success && <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontWeight: 500 }}>✓ {success}</div>}
        {error   && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontWeight: 500 }}>✗ {error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>Loading billing info…</div>
        ) : (
          <>
            {/* Overdue invoices banner — persists until ALL invoices are paid,
                regardless of whether the superadmin has manually re-activated the account. */}
            {outstanding.length > 0 && (
              <div style={{ background: '#FEF2F2', border: '2px solid #F87171', borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
                <div style={{ fontWeight: 800, fontSize: 17, color: '#991B1B', marginBottom: 6 }}>
                  🔒 {outstanding.length} overdue invoice{outstanding.length !== 1 ? 's' : ''} — Total ${outstanding.reduce((s, p) => s + (p.total_amount_usd || p.amount_usd || 0), 0).toFixed(2)}
                </div>
                <p style={{ margin: '0 0 12px', color: '#7F1D1D', fontSize: 14, lineHeight: 1.6 }}>
                  Your account has unpaid subscription invoices{tenant?.account_status === 'on_hold' ? ' and is currently on hold' : ''}. Please pay all outstanding invoices and upload proof to keep your account fully active. This notice clears automatically once every invoice is paid.
                </p>
                <button onClick={() => setTab('outstanding')}
                  style={{ padding: '9px 18px', borderRadius: 8, background: '#DC2626', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  View &amp; pay outstanding invoices →
                </button>
              </div>
            )}
            {/* Student limit warning */}
            {maxStudents > 0 && studentCount >= maxStudents && (
              <div style={{ background: '#FEF2F2', border: '2px solid #F87171', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>🔴 Student Limit Reached</div>
                <p style={{ margin: 0, color: '#7F1D1D', fontSize: 14 }}>
                  You have {studentCount} of {maxStudents} students enrolled. You cannot add more students until you upgrade your plan and the upgrade is confirmed by SpeakIQ.
                </p>
                <button onClick={() => setTab('plan')} style={{ marginTop: 10, background: C.error, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  Upgrade Plan →
                </button>
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `2px solid ${C.border}`, overflowX: 'auto' }}>
              {TABS.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  padding: '10px 18px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
                  background: 'transparent', whiteSpace: 'nowrap',
                  color: tab === t.id ? C.teal : C.grey,
                  borderBottom: tab === t.id ? `2px solid ${C.teal}` : '2px solid transparent',
                  marginBottom: -2,
                }}>{t.label}</button>
              ))}
            </div>

            {/* ── TAB: Plan & Limits ─────────────────────────────────────── */}
            {tab === 'plan' && (
              <>
                {/* Current plan summary */}
                <div style={{ background: C.white, borderRadius: 14, border: `2px solid ${C.teal}`, padding: 24, marginBottom: 24, boxShadow: `0 4px 20px ${C.teal}18` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Current Plan</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <span style={{ fontSize: 24, fontWeight: 800, color: C.dark }}>{PLAN_META[tenant?.plan]?.name || tenant?.plan || '—'}</span>
                        <span style={{ background: C.teal + '20', color: C.teal, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{tenant?.plan_tier?.toUpperCase()}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: C.teal, marginBottom: 4 }}>${tenant?.monthly_fee_usd}<span style={{ fontSize: 13, fontWeight: 400, color: C.grey }}>/month</span></div>
                      {tenant?.pending_plan && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 12px', background: '#FFF7ED', borderRadius: 8, border: '1px solid #FED7AA' }}>
                          <span style={{ fontSize: 12 }}>⏳</span>
                          <span style={{ fontSize: 13, color: '#92400E' }}>
                            Plan change to <strong>{PLAN_META[tenant.pending_plan]?.name || tenant.pending_plan}</strong> pending super admin approval.
                          </span>
                          <button onClick={handleCancelPlanChange} style={{ marginLeft: 'auto', fontSize: 11, color: C.error, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 200, flex: 1 }}>
                      <UsageBar current={studentCount} max={maxStudents} />
                    </div>
                  </div>
                </div>

                {/* Plan grid */}
                <h3 style={{ margin: '0 0 16px', fontSize: 17, color: C.dark }}>Available Plans</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap: 16, marginBottom: 24 }}>
                  {Object.entries(plans).sort(([,a],[,b]) => (a.monthly_usd||0) - (b.monthly_usd||0)).map(([planId, info]) => (
                    <PlanCard
                      key={planId}
                      planId={planId}
                      info={info}
                      meta={PLAN_META[planId]}
                      isCurrent={planId === tenant?.plan}
                      isPending={planId === tenant?.pending_plan}
                      onSelect={handlePlanSelect}
                    />
                  ))}
                </div>
                <div style={{ background: '#F0F7FA', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: C.grey }}>
                  ℹ️ Plan changes require confirmation by SpeakIQ after payment. Your current plan remains active until the new plan is confirmed.
                </div>
              </>
            )}

            {/* ── TAB: Billing & Payment ─────────────────────────────────── */}
            {tab === 'billing' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 20, marginBottom: 24 }}>
                  {/* Current Invoice */}
                  <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.dark }}>Current Invoice</h3>
                    {payment ? (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: status.bg, color: status.color }}>{status.icon} {status.label}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontSize: 24, fontWeight: 800, color: C.teal }}>${(payment.total_amount_usd || payment.amount_usd)?.toFixed(2)}</span>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button onClick={() => previewInvoice(payment.id)}
                                style={{ padding: '6px 14px', borderRadius: 8, background: '#F0F7FA', color: C.navy, border: '1px solid ' + C.border, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                👁 Preview Invoice
                              </button>
                              <button onClick={() => downloadInvoice(payment.id, payment.billing_month)}
                                style={{ padding: '6px 14px', borderRadius: 8, background: C.teal, color: 'white', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                ⬇ Download
                              </button>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Row label="Billing Month" value={payment.billing_month} />
                          <Row label="Due Date" value={payment.due_date ? new Date(payment.due_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'} />
                          <Row label="Plan" value={`${PLAN_META[tenant?.plan]?.name || tenant?.plan || '—'} (${tenant?.plan_tier || ''})`} />
                          {payment.is_plan_change && payment.new_plan && (
                            <div style={{ background: '#FFF7ED', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#92400E', fontWeight: 600 }}>
                              🔄 Plan change → {PLAN_META[payment.new_plan]?.name || payment.new_plan}
                            </div>
                          )}
                          {(payment.addon_charges || []).length > 0 && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid ' + C.border }}>
                              <Row label="Subscription" value={"$" + (payment.amount_usd || 0).toFixed(2)} />
                              {(payment.addon_charges || []).map((a, i) => (
                                <Row key={i} label={"+ " + a.label} value={"$" + (a.amount_usd || 0).toFixed(2)} />
                              ))}
                              <Row label="Total Due" value={"$" + (payment.total_amount_usd || payment.amount_usd || 0).toFixed(2)} highlight />
                            </div>
                          )}
                          {payment.status === 'confirmed' && <Row label="Confirmed" value={payment.confirmed_at ? new Date(payment.confirmed_at).toLocaleDateString('en-GB') : '—'} highlight />}
                          {payment.status === 'rejected' && payment.rejected_reason && (
                            <div style={{ background: '#FEE2E2', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#991B1B', marginTop: 4 }}>
                              <strong>Reason:</strong> {payment.rejected_reason}
                            </div>
                          )}
                        </div>
                      </>
                    ) : <p style={{ color: C.grey, fontSize: 14 }}>No invoice for this month.</p>}
                  </div>

                  {/* Upload proof */}
                  <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: C.dark }}>Upload Payment Proof</h3>
                    <p style={{ fontSize: 13, color: C.grey, margin: '0 0 16px' }}>After transferring, upload your receipt below.</p>
                    {canUpload ? (
                      <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${uploading ? C.grey : C.teal}`, borderRadius: 12, padding: '28px 20px', textAlign: 'center', cursor: uploading ? 'not-allowed' : 'pointer', background: '#F0FBFF' }}>
                        <div style={{ fontSize: 36, marginBottom: 8 }}>{uploading ? '⏳' : '📤'}</div>
                        <div style={{ fontWeight: 600, color: C.teal, fontSize: 14, marginBottom: 4 }}>{uploading ? 'Uploading…' : 'Click to upload receipt'}</div>
                        <div style={{ fontSize: 12, color: C.grey }}>PNG, JPG, PDF, HEIC — max 10MB</div>
                        <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.pdf,.heic,.webp" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
                      </div>
                    ) : payment?.status === 'proof_uploaded' ? (
                      <div style={{ background: '#EFF6FF', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
                        <div style={{ fontWeight: 600, color: '#1E40AF', fontSize: 14 }}>Proof submitted — awaiting review</div>
                        <div style={{ fontSize: 12, color: '#3B82F6', marginTop: 4 }}>Usually confirmed within 1 business day</div>
                      </div>
                    ) : payment?.status === 'confirmed' ? (
                      <div style={{ background: '#F0FDF4', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                        <div style={{ fontWeight: 600, color: '#065F46', fontSize: 14 }}>Payment confirmed — thank you!</div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Bank details */}
                {bank ? (
                  <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `2px solid ${C.teal}30`, marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: C.dark }}>💳 Payment Details</h3>
                    <p style={{ margin: '0 0 18px', fontSize: 13, color: C.grey }}>Pay your subscription using any of the methods below, then upload your receipt above.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', gap: 12 }}>
                      {(bank.account_number || bank.iban || bank.bank_name) && (<>
                        {bank.account_holder && <BankRow label="Account Holder" value={bank.account_holder} />}
                        {bank.bank_name && <BankRow label="Bank Name" value={bank.bank_name} />}
                        {bank.account_number && <BankRow label="Account Number" value={bank.account_number} mono copy />}
                        {bank.iban && <BankRow label="IBAN" value={bank.iban} mono copy />}
                        {bank.sort_code && <BankRow label="Sort Code" value={bank.sort_code} mono copy />}
                        {bank.swift_bic && <BankRow label="SWIFT / BIC" value={bank.swift_bic} mono copy />}
                        {bank.currency && <BankRow label="Currency" value={bank.currency} />}
                      </>)}
                      {bank.paypal_email && <BankRow label="💙 PayPal Email" value={bank.paypal_email} copy fullWidth />}
                      {bank.wise_email && <BankRow label="🟢 Wise Email" value={bank.wise_email} copy fullWidth />}
                    </div>
                    {bank.payment_notes && <div style={{ marginTop: 14, padding: '10px 14px', background: '#F8FAFC', borderRadius: 8, fontSize: 13, color: C.dark, borderLeft: `3px solid ${C.teal}` }}><strong>Note:</strong> {bank.payment_notes}</div>}
                    <div style={{ marginTop: 12, padding: '10px 14px', background: '#FFFBEB', borderRadius: 8, fontSize: 13, color: '#92400E' }}>
                      <strong>Reference:</strong> Include "<strong>{tenant?.name}</strong>" and "<strong>{payment?.billing_month}</strong>" in your transfer reference.
                    </div>
                    {bank.crypto_enabled && bank.crypto_wallet && (
                      <div style={{ marginTop: 16, padding: 16, border: '2px solid #10B981', borderRadius: 10, background: '#ECFDF5' }}>
                        <div style={{ fontWeight: 800, color: '#065F46', marginBottom: 10 }}>₮ Pay in USDC (crypto) — fast alternative</div>
                        {bank.crypto_network && <BankRow label="Network" value={bank.crypto_network} />}
                        <BankRow label="USDC Wallet" value={bank.crypto_wallet} mono copy fullWidth />
                        {bank.crypto_qr && (
                          <div style={{ textAlign: 'center', margin: '12px 0' }}>
                            <img src={bank.crypto_qr} alt="USDC wallet QR" style={{ width: 168, height: 168, background: '#fff', padding: 8, borderRadius: 10, border: '1px solid #A7F3D0' }} />
                            <div style={{ fontSize: 11, color: '#065F46', marginTop: 4 }}>Scan to pay in USDC</div>
                          </div>
                        )}
                        {bank.crypto_notes && <div style={{ marginTop: 10, fontSize: 13, color: '#065F46', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{bank.crypto_notes}</div>}
                        <div style={{ marginTop: 8, fontSize: 12, color: '#065F46' }}>Send the exact USD amount in USDC to the wallet above, then upload the transaction hash as your payment proof.</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ background: '#FEF3C7', borderRadius: 12, padding: 20, marginBottom: 24, border: '1px solid #FCD34D' }}>
                    <p style={{ margin: 0, color: '#92400E', fontWeight: 500 }}>⚠️ Payment details not yet configured. Contact SpeakIQ support.</p>
                  </div>
                )}
              </>
            )}

            {/* ── TAB: Payment History ───────────────────────────────────── */}
            {tab === 'history' && (
              <div style={{ background: C.white, borderRadius: 14, padding: 24, border: `1px solid ${C.border}` }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.dark }}>Payment History</h3>
                {data?.history?.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Month', 'Type', 'Amount', 'Due Date', 'Status', 'Confirmed', 'Invoice'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: C.grey, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.history.map(p => {
                          const s = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
                          return (
                            <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.billing_month}</td>
                              <td style={{ padding: '10px 12px' }}>
                                {p.is_plan_change ? <span style={{ fontSize: 11, background: '#FFF7ED', color: '#92400E', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>PLAN CHANGE</span> : <span style={{ fontSize: 11, color: C.grey }}>Monthly</span>}
                              </td>
                              <td style={{ padding: '10px 12px', color: C.teal, fontWeight: 700 }}>${p.amount_usd?.toFixed(2)}</td>
                              <td style={{ padding: '10px 12px', color: C.grey }}>{p.due_date ? new Date(p.due_date + 'T00:00:00').toLocaleDateString('en-GB') : '—'}</td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: s.bg, color: s.color }}>{s.icon} {s.label}</span>
                              </td>
                              <td style={{ padding: '10px 12px', color: C.grey, fontSize: 12 }}>
                                {p.confirmed_at ? new Date(p.confirmed_at).toLocaleDateString('en-GB') : '—'}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button onClick={() => previewInvoice(p.id)}
                                    style={{ padding: '4px 8px', borderRadius: 5, background: '#F0F7FA', color: C.navy, border: '1px solid ' + C.border, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                                    👁
                                  </button>
                                  <button onClick={() => downloadInvoice(p.id, p.billing_month)}
                                    style={{ padding: '4px 8px', borderRadius: 5, background: C.teal, color: 'white', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                                    ⬇
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <p style={{ color: C.grey, fontSize: 14 }}>No payment history yet.</p>}
              </div>
            )}
            {tab === 'outstanding' && (
              <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #E5E7EB' }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#0D1B2A' }}>Outstanding Invoices</h3>
                <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
                  All unpaid months are listed below. Upload payment proof for each to clear your balance.
                </p>
                {outstanding.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 15 }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    You are fully up to date — no outstanding invoices.
                  </div>
                ) : (
                  <>
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#991B1B', fontWeight: 700, fontSize: 14 }}>
                        🚨 {outstanding.length} outstanding invoice{outstanding.length !== 1 ? 's' : ''} — Total: ${outstanding.reduce((s,p) => s + (p.total_amount_usd || p.amount_usd || 0), 0).toFixed(2)}
                      </span>
                      <span style={{ color: '#991B1B', fontSize: 12 }}>Upload proof for each to restore full access</span>
                    </div>
                    {outstanding.map(p => {
                      const s = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
                      const canUp = ['pending','overdue','rejected'].includes(p.status);
                      const totalAmt = (p.total_amount_usd || p.amount_usd || 0).toFixed(2);
                      const addons = p.addon_charges || [];
                      return (
                        <div key={p.id} style={{ border: `2px solid ${p.is_overdue ? '#FCA5A5' : '#E5E7EB'}`, borderRadius: 14, padding: 20, marginBottom: 16, background: p.is_overdue ? '#FFF5F5' : '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: addons.length > 0 ? 12 : 0 }}>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: 18, color: '#0D1B2A' }}>{p.billing_month}</div>
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                                Due: {p.due_date ? new Date(p.due_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                                {p.days_late > 0 && <span style={{ color: '#EF4444', fontWeight: 700, marginLeft: 8 }}>· {p.days_late} days overdue</span>}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 26, fontWeight: 900, color: '#00B4D8' }}>${totalAmt}</div>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color }}>{s.icon} {s.label}</span>
                            </div>
                          </div>

                          {/* Breakdown */}
                          <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 6, borderBottom: addons.length > 0 ? '1px solid #E5E7EB' : 'none' }}>
                              <span style={{ color: '#6B7280' }}>Base subscription</span>
                              <span style={{ fontWeight: 600 }}>${(p.amount_usd || 0).toFixed(2)}</span>
                            </div>
                            {addons.map((a, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, fontSize: 12 }}>
                                <span style={{ color: '#6B7280' }}>+ {a.label}</span>
                                <span style={{ fontWeight: 600, color: '#F59E0B' }}>${(a.amount_usd || 0).toFixed(2)}</span>
                              </div>
                            ))}
                            {addons.length > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #E5E7EB', fontWeight: 700 }}>
                                <span>Total due</span>
                                <span style={{ color: '#00B4D8' }}>${totalAmt}</span>
                              </div>
                            )}
                          </div>

                          {p.status === 'rejected' && p.rejected_reason && (
                            <div style={{ background: '#FEE2E2', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991B1B' }}>
                              ❌ Previous proof rejected: {p.rejected_reason}. Please upload a new proof.
                            </div>
                          )}

                          {canUp && (
                            <div>
                              <input type="file" accept="image/*,.pdf,.heic" style={{ display: 'none' }} id={`proof-${p.id}`}
                                onChange={async e => {
                                  const file = e.target.files?.[0]; if (!file) return;
                                  const fd = new FormData(); fd.append('file', file);
                                  setUploadingId(p.id); setError(''); setSuccess('');
                                  try {
                                    await api.post(`/api/admin/billing/${p.id}/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    setSuccess(`Proof uploaded for ${p.billing_month}! We will verify within 1 business day.`);
                                    load();
                                  } catch (ex) { setError(ex?.response?.data?.error || 'Upload failed. Please try again.'); }
                                  setUploadingId(null);
                                }} />
                              <label htmlFor={`proof-${p.id}`} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '11px 22px', borderRadius: 10,
                                background: uploadingId === p.id ? '#9CA3AF' : '#00B4D8',
                                color: '#fff', cursor: uploadingId === p.id ? 'not-allowed' : 'pointer',
                                fontSize: 14, fontWeight: 700, transition: 'opacity 0.15s',
                              }}>
                                {uploadingId === p.id ? '⏳ Uploading...' : '📎 Upload Payment Proof for ' + p.billing_month}
                              </label>
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>
                                Transfer ${totalAmt} and upload your bank receipt (PNG, JPG, PDF — max 10MB)
                              </div>
                            </div>
                          )}
                          {p.status === 'proof_uploaded' && (
                            <div style={{ color: '#2563EB', fontSize: 13, fontWeight: 600 }}>
                              📎 Proof submitted — awaiting SpeakIQ confirmation
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}

          </>
        )}
      </div>

      {/* Plan change confirmation modal */}
      {selectedPlan && (
        <ConfirmPlanModal
          currentPlan={tenant}
          newPlanId={selectedPlan.planId}
          newPlanInfo={selectedPlan.info}
          newPlanMeta={PLAN_META[selectedPlan.planId]}
          loading={planLoading}
          onConfirm={handlePlanConfirm}
          onCancel={() => setSelectedPlan(null)}
        />
      )}
      {/* Invoice Preview Modal */}
      {previewUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
          <div style={{ background: '#0D1B2A', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ color: '#00B4D8', fontWeight: 800, fontSize: 16 }}>Invoice Preview</span>
            <div style={{ display: 'flex', gap: 10 }}>
              <a href={previewUrl} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', borderRadius: 8, background: '#00B4D8', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                ↗ Open Full Page
              </a>
              <a href={previewUrl + '?fmt=pdf'} download style={{ padding: '8px 16px', borderRadius: 8, background: '#10B981', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                ⬇ Download PDF
              </a>
              <button onClick={() => setPreviewUrl(null)} style={{ padding: '8px 16px', borderRadius: 8, background: '#374151', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                ✕ Close
              </button>
            </div>
          </div>
          <iframe src={previewUrl + '?embed=1'} style={{ flex: 1, border: 'none', width: '100%' }} title="Invoice Preview" />
        </div>
      )}
    </Layout>
  );
}
